import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FrameProofLink } from 'site-modules/shared/components/ai-link/frame-proof-link';
import { Storage } from 'site-modules/shared/utils/storage';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';

import './co-driver-banner.scss';

const CREATIVE_ID = 'codriver-banner';
const CO_DRIVER_BANNER_DISMISSED = 'co_driver_banner_dismissed';
const BOTTOM_THRESHOLD = 100;
export const INPUT_PLACEHOLDER = 'Ask anything';

export const CoDriverBanner = memo(({ isMobile }) => {
  const sessionStorage = useRef(new Storage('sessionStorage'));

  const [isVisible, setIsVisible] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(false);

  const showBanner = useCallback(() => {
    setIsVisible(true);

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
      event_data: {
        action_name: TrackingConstant.ACTION_WIDGET_VIEW,
        action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
        total_page_height: Math.max(document.body.scrollHeight, document.body.offsetHeight),
        creative_id: CREATIVE_ID,
      },
    });
  }, []);

  const handleClick = useCallback(() => {
    sessionStorage.current.set(CO_DRIVER_BANNER_DISMISSED, true);
  }, [sessionStorage]);

  const handleClose = useCallback(() => {
    sessionStorage.current.set(CO_DRIVER_BANNER_DISMISSED, true);
    setIsDismissing(true);
  }, [sessionStorage]);

  const handleAnimationEnd = useCallback(() => {
    if (isDismissing) {
      setIsVisible(false);
    }
  }, [isDismissing]);

  useEffect(() => {
    const visible =
      sessionStorage.current.storageInitialized() && !sessionStorage.current.get(CO_DRIVER_BANNER_DISMISSED);

    if (!visible) return undefined;

    window.addEventListener('scroll', showBanner, { once: true, passive: true });

    return () => window.removeEventListener('scroll', showBanner);
  }, [showBanner]);

  useEffect(() => {
    if (!isMobile || !isVisible || isDismissing) return undefined;

    const updateScrollPosition = () => {
      const pageHeight = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      setIsNearBottom(pageHeight - window.innerHeight - window.scrollY <= BOTTOM_THRESHOLD);
    };
    updateScrollPosition();
    window.addEventListener('scroll', updateScrollPosition, { passive: true });
    window.addEventListener('resize', updateScrollPosition);

    return () => {
      window.removeEventListener('scroll', updateScrollPosition);
      window.removeEventListener('resize', updateScrollPosition);
    };
  }, [isMobile, isVisible, isDismissing]);

  if (!isVisible) {
    return null;
  }

  const isCompact = isMobile && isNearBottom;

  return (
    <div
      className={classnames('co-driver-banner-wrapper w-100 fixed-bottom', {
        dismissing: isDismissing,
        'near-bottom': isCompact,
      })}
      data-tracking-parent={CREATIVE_ID}
      data-no-widget-view
      role="region"
      aria-label="AI Assistant banner"
      onAnimationEnd={handleAnimationEnd}
    >
      <FrameProofLink
        className="co-driver-card-link banner pos-r d-flex text-decoration-none rounded-12 w-100 with-input"
        ariaLabel="Open AI Assistant chat"
        onClick={handleClick}
      >
        <span className="co-driver-card-content d-flex flex-column w-100 px-1 pt-1 pb-1 pb-md-1_25 text-blue-100 rounded-12 align-items-center">
          <span className="co-driver-banner w-100">
            <span className="banner-heading d-flex align-items-center justify-content-start flex-shrink-0 mb-1 pos-r">
              <i className="icon icon-edmunds-car size-20 me-0_25 pos-a left-0 bottom-0" aria-hidden />
              <span className="fw-bold size-14 ms-1_5">AI Assistant</span>
            </span>
            <span className="d-flex align-items-md-center justify-content-center w-100 flex-column flex-md-row">
              <span
                className="banner-copy text-white fw-bold w-100 me-md-3 me-1_75 size-24 mb-1 mb-md-0"
                aria-hidden={isCompact}
              >
                Get the best price. Ask me how.
              </span>
              <span
                className={classnames(
                  'co-driver-input',
                  isMobile ? 'pos-a bottom-0 left-0 right-0 mx-1 mb-0_5' : 'pos-r w-100'
                )}
              >
                <span className="co-driver-input-copy w-100 bg-white text-cool-gray-50 ps-1 pe-0_25 d-flex align-items-center justify-content-between">
                  {INPUT_PLACEHOLDER}
                  <span
                    className="co-driver-submit bg-cool-gray-60 d-flex align-items-center justify-content-center"
                    aria-hidden="true"
                  >
                    <i className="icon icon-arrow-up8 size-20 text-white" aria-hidden />
                  </span>
                </span>
              </span>
            </span>
          </span>
        </span>
      </FrameProofLink>
      <button
        type="button"
        className="co-driver-banner-close pos-a border-0 bg-transparent"
        aria-label="Close AI Assistant banner"
        onClick={handleClose}
        data-tracking-id="co_driver_banner_dismiss"
      >
        <i
          className="d-inline-block icon icon-cross2 size-12 text-cool-gray-10 bg-cool-gray-80 rounded-circle text-center"
          aria-hidden
        />
      </button>
    </div>
  );
});

CoDriverBanner.displayName = 'CoDriverCardBanner';
CoDriverBanner.propTypes = {
  isMobile: PropTypes.bool.isRequired,
};
